// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import { Icon as NeoIcon } from '@locus-taxy/neo-ui';
import classnames from 'classnames';
import TimeUtils, { DateTimeFormat } from 'common/utils/TimeUtils';
import {
    PopoverPlaceEnum,
    PopoverTriggerEnum
} from 'common/components/core/popover/popOverFactory';
import { ButtonTypes } from 'common/components/core/button';
import { DivWithPopover } from 'common/components/core/wrappers/Div';
import theme from 'common/components/core/full_screen_icon/FullScreenIcon.scss';
import UiSwitcherPopup from 'common/components/core/ui_switcher/UiSwitcherPopup';
import uiSwitchTheme from 'common/components/core/ui_switcher/UiSwitcherPopup.scss';
import UISwitcherActions from 'common/redux/actions/uiSwitcher';
import { getV2UIStatus } from 'common/redux/selectors/appPreferences';
import {
    getV2LastNotified,
    getV2PopupState,
    isReadyToShowV2Popup,
    getIsV2PopupAcknowledged,
    getIsV1PopupAcknowledged
} from 'common/redux/selectors/uiSwitcher';
import { UISwitcherStateEnum as UI_SWICHER_STATES } from 'common/enums/UISwitcher';
import UiSwitcherIds from 'common/tests/common/UiSwitcher.json';
import Products from 'common/enums/Product';

type OwnProps = {
    'data-test-id': string,
    onUpgrade?: () => void,
    onRevert?: () => void,
    product: ProductType
};
type Props = {
    ...OwnProps,
    v2LastNotified: string | null,
    v2PopupState: string | null,
    isV2UIActive: boolean,
    isReadyToShowV2Popup: boolean,
    readyToShowV2Popup: (show: boolean, product: ProductType) => void,
    updateV2UILastNotified: (today: string, product: ProductType) => void,
    isV2PopupAcknowledged: boolean,
    isV1PopupAcknowledged: boolean
};

type State = {
    isPopoverOpen: boolean
};

const getContainerClassName = (isPopoverOpen: boolean) => {
    return classnames(uiSwitchTheme.iconContainer, {
        [uiSwitchTheme.iconContainerActive]: isPopoverOpen
    });
};

class UiSwitcher extends React.PureComponent<Props, State> {
    popoverRef: any;
    constructor(props: Props) {
        super(props);
        this.state = { isPopoverOpen: false };
    }

    componentDidMount() {
        this.handlePopupVisibility();
    }

    componentDidUpdate() {
        this.handlePopupVisibility();
    }

    handlePopupVisibility() {
        if (this.props.isReadyToShowV2Popup) {
            const { readyToShowV2Popup, product } = this.props;
            const showUpgradePopup = this.shouldShowPopup();
            if (showUpgradePopup) {
                this.handleShowPopover();
                readyToShowV2Popup(false, product);
            }
        }
    }

    shouldShowPopup() {
        const {
            v2LastNotified,
            isV2UIActive,
            v2PopupState,
            isV2PopupAcknowledged,
            isV1PopupAcknowledged,
            product
        } = this.props;

        if (product === Products.DISPATCHER_PLANNER) {
            return false;
        }
        if (
            !isV1PopupAcknowledged &&
            v2PopupState === UI_SWICHER_STATES.JUST_REVERTED
        ) {
            return true;
        }

        if (
            !isV2PopupAcknowledged &&
            v2PopupState === UI_SWICHER_STATES.NEW_UI
        ) {
            return true;
        }

        const today = TimeUtils.now(true);

        if (!v2LastNotified && !isV2UIActive) {
            return true;
        }
        const lastNotified = TimeUtils.format(
            v2LastNotified,
            DateTimeFormat.LONG_DATE_FORMAT
        );
        if (!isV2UIActive && TimeUtils.isAfter(today, lastNotified)) {
            return true;
        }

        return false;
    }

    updateLastNotified() {
        const { updateV2UILastNotified, product } = this.props;
        const today: any = TimeUtils.now(true);
        updateV2UILastNotified(today, product);
    }
    onDismiss = () => {
        this.updateLastNotified();
        this.handleClosePopover();
    };
    onUpgrade = () => {
        const { onUpgrade: onUpgradeCallback } = this.props;
        this.updateLastNotified();
        if (onUpgradeCallback) onUpgradeCallback();
    };
    onRevert = () => {
        const { onRevert: onRevertCallback } = this.props;
        this.updateLastNotified();
        if (onRevertCallback) onRevertCallback();
    };
    onHide = () => {
        this.setState({ isPopoverOpen: false });
    };
    onShow = () => {
        this.setState({ isPopoverOpen: true });
    };
    handleClosePopover = () => {
        if (this.popoverRef) {
            this.popoverRef.getWrappedInstance().hidePopover();
        }
    };
    handleShowPopover = () => {
        if (this.popoverRef) {
            this.popoverRef.getWrappedInstance().showPopover();
        }
    };
    getPopoverRef = (ref: any) => {
        if (ref) {
            this.popoverRef = ref;
        }
    };
    renderNotificationBubble() {
        if (this.props.isV2UIActive) {
            return null;
        }
        return <div className={uiSwitchTheme.notificationBubble} />;
    }
    render() {
        const { product } = this.props;
        const testId = `${product}-${UiSwitcherIds.uiSwitcher}`;
        const popOverContent = (
            <UiSwitcherPopup
                onDismiss={this.onDismiss}
                onUpgrade={this.onUpgrade}
                onRevert={this.onRevert}
                product={product}
            />
        );
        return (
            <DivWithPopover
                ref={this.getPopoverRef}
                className={getContainerClassName(this.state.isPopoverOpen)}
                disableAnimation
                enableAntialias
                theme={theme}
                disableArrow
                triggerPopoverOn={PopoverTriggerEnum.Click}
                popOverClassName={uiSwitchTheme.popOver}
                place={PopoverPlaceEnum.Below}
                popOverContent={popOverContent}
                buttonType={ButtonTypes.Icon}
                hideOnOuterClick={false}
                onClick={this.handleShowPopover}
                onHide={this.onHide}
                onShow={this.onShow}
                data-test-id={testId}
            >
                {this.renderNotificationBubble()}
                <NeoIcon icon="switch_ui" size={NeoIcon.Size.medium} />
            </DivWithPopover>
        );
    }
}

const withRedux = connect<Props, OwnProps, _, _, _, _>(
    (state, props) => {
        return {
            isV2UIActive: getV2UIStatus(state, props.product),
            v2LastNotified: getV2LastNotified(state, props.product),
            v2PopupState: getV2PopupState(state, props.product),
            isReadyToShowV2Popup: isReadyToShowV2Popup(state, props.product),
            isV2PopupAcknowledged: getIsV2PopupAcknowledged(
                state,
                props.product
            ),
            isV1PopupAcknowledged: getIsV1PopupAcknowledged(
                state,
                props.product
            )
        };
    },
    {
        updateV2UILastNotified: UISwitcherActions.updateV2UILastNotified,
        readyToShowV2Popup: UISwitcherActions.readyToShowV2Popup
    }
);

const ConnectedSwitcher = withRedux(UiSwitcher);

export default (ConnectedSwitcher: React.AbstractComponent<OwnProps>);
